import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useAuthContext } from '../context/auth.context'
import { useApiFormLaunch, useApiListAuthOrganization } from '../api-new/state/auth'
import { useApiFormLaunchCheck } from '../api-new/state/content'
import { AuthHead, AuthMark, AuthPage } from '../shared/sso-workspace/auth-card'
import { orgKey } from '../shared/sso-workspace/organization-chooser'
import { ErrorReference } from '../shared/error/error-reference'
import { serverMessage } from '../shared/error/server-message'
import { decodeJwtClaims } from '../shared/jwt'
import { getAccessToken } from '../storage/auth'

// /launch/content/:nodeID (owner 2026-10-08): THE way into the web viewer
// for a course. The marketing site's /open page, the admin's Launch, shared
// course links and the player's own redirect when a deep link has no session
// all come here; only a visit to the viewer itself signs in there
// ("everything else federates from the admin").
//
// Signed out, the admin's normal sign-in (SSO included) comes first and
// returns here with the query kept (the signed-out "/" route sends the path
// along, and it is not an admin section, so a learner is sent back too).
// Signed in -- any role, a learner with no admin permission included:
//   1. Ask content whether the course can be opened (GET
//      /api/ddd/launch-check/:nodeID: the one access rule storage's
//      download follows too). Content works out the course's organization
//      (owner 2026-10-13): when the person's login is a member of it and
//      their session is elsewhere it answers switch_org with that
//      organization, and the bridge switches to it (the organization list
//      and switch the account page use) and asks again there. An
//      organization missing from their list reads as "You can't open this
//      course"; a second switch_org is never followed.
//   2. Say why not in plain words: no web build, removed, not shared with
//      them (ask their organization's admin), or not one they can open (an
//      organization's public-library setting is never overridden). A failed
//      check never blocks the launch.
//   3. Mint a launch code for the person's OWN session (auth's POST
//      /api/v1/launch, the path of a course's Launch) and replace this page
//      with the viewer's URL. A failed mint says why, with its reference.
//
// ?org=<organizationID> is ignored: old links and printed QR codes still
// carry it, so a numeric one keeps the link valid, but it is never a gate,
// never switched to, and never the app link's organization -- the "Open in
// the app" link names the organization the bridge resolved (the session's,
// or the one it switched to).

const OPENING = 'Opening the course in your browser…'
const FAILED = "Couldn't open the web viewer. Please try again."
const ID = /^[1-9][0-9]{0,18}$/

const appLink = (nodeID, org) => `edxr://content?id=${encodeURIComponent(nodeID)}${org ? `&org=${encodeURIComponent(org)}` : ''}`

const NOT_OPENABLE = {
  no_web_build: { title: "This course isn't on the web yet", body: 'Open it in the EducationXR app instead.' },
  removed: { title: 'This course was removed', body: 'It is no longer in your organization. Ask whoever sent the link for another course.' },
  // a course of their organization that isn't shared with them (content's
  // one access rule, owner 2026-10-09): they hold a link, so it isn't hidden
  no_access: { title: "You don't have access to this course", body: "Ask your organization's admin." },
  unavailable: {
    title: "You can't open this course",
    body: "It isn't available to your account, or it no longer exists. Ask whoever sent the link, or check you're signed in with the right account."
  }
}

const SWITCH_ORG = 'switch_org'

export const LaunchBridge = () => {
  const { nodeID } = useParams()
  const { search } = useLocation()
  const { state: auth, setAuthToken } = useAuthContext()
  const apiFormLaunch = useApiFormLaunch()
  const apiFormLaunchCheck = useApiFormLaunchCheck()
  const apiListAuthOrganization = useApiListAuthOrganization()
  const [outcome, setOutcome] = useState(null)
  const started = useRef(false)

  const orgParam = new URLSearchParams(search).get('org')
  const valid = ID.test(nodeID || '') && (orgParam === null || ID.test(orgParam))
  const [org, setOrg] = useState(() => (decodeJwtClaims(getAccessToken()) || {}).organizationID)

  useEffect(() => {
    if (!valid || started.current) return
    started.current = true
    // Who and where from the session's own token, read when used: the
    // context's claims arrive a render later, and a switch replaces them.
    const claims = () => decodeJwtClaims(getAccessToken()) || {}
    const check = async () => {
      try {
        return (await apiFormLaunchCheck.get(nodeID)) || {}
      } catch (e) {
        // the check is a courtesy: the viewer decides in the end
        return {}
      }
    }
    const run = async () => {
      let inOrg = claims().organizationID
      let answer = await check()
      if (answer.status === SWITCH_ORG) {
        const organizations = (await apiListAuthOrganization.get(auth.refreshToken)) || []
        const target = organizations.find(o => orgKey(o) === String(answer.organizationId) && !o.scheduledForDeletion)
        if (!target) {
          setOutcome({ kind: 'unavailable' })
          return
        }
        setAuthToken(await apiListAuthOrganization.put(auth.refreshToken, target))
        inOrg = claims().organizationID || Number(answer.organizationId)
        setOrg(inOrg)
        answer = await check()
        // never follow a second switch: the switch didn't take
        if (answer.status === SWITCH_ORG) answer = { status: 'unavailable' }
      }
      if (NOT_OPENABLE[answer.status]) {
        setOutcome({ kind: answer.status })
        return
      }
      const result = await apiFormLaunch.post({ user: claims().loginID, org: inOrg, target: `content/${nodeID}` })
      if (!result || typeof result.url !== 'string' || !result.url) throw new Error('The launch answer has no URL.')
      window.location.replace(result.url)
    }
    run().catch(e => setOutcome({ kind: 'failed', message: serverMessage(e, FAILED), error: e }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, nodeID])

  const ways = (
    <div className="sa-actions">
      {valid && <a className="btn sw-btn-primary sa-block" href={appLink(nodeID, org)}>Open in the app</a>}
      <Link className={`btn ${valid ? 'sw-btn' : 'sw-btn-primary'} sa-block`} to="/download/links">Get the app</Link>
    </div>
  )

  const page = (title, tone, body, extra) =>
    <AuthPage labelledBy="launch-title">
      <AuthHead id="launch-title" title={title} mark={<AuthMark tone={tone}>!</AuthMark>} />
      <p className="m-0">{body}</p>
      {extra}
      {ways}
    </AuthPage>

  if (!valid) return page("This link doesn't name a course", 'warn', 'Ask whoever sent it for the course link again, or open the course in the app.')
  if (outcome && outcome.kind === 'failed') {
    return page("Couldn't open this course in your browser", 'bad', outcome.message, <ErrorReference error={outcome.error} />)
  }
  if (outcome) return page(NOT_OPENABLE[outcome.kind].title, 'warn', NOT_OPENABLE[outcome.kind].body)

  return (
    <AuthPage labelledBy="launch-title">
      <AuthHead id="launch-title" title="Web viewer" />
      <p className="m-0" role="status">{OPENING}</p>
    </AuthPage>
  )
}
